import React, { useCallback, useEffect, useRef } from 'react';
import styles from 'components/compose-input-field/styles/ComposeInputField.module.scss';
import { KiteButton, KiteIcon } from '@kite/react-kite';
import { useAppSelector, useAppDispatch } from 'hooks';
import { addCc, removeCcByIndex, resetCc, updateComposeFormError, updateIsCcFieldEnabled } from 'slices/compose';
import ReactTags, { Tag } from 'react-tag-autocomplete';
import splitEmailDisplayName from 'utils/splitEmailDisplayName';
import useContactSearch from 'hooks/useContactSearch';
import getSuggestionComponent from 'components/compose-input-field/ContactSuggestion';
import { getDisplayNameFallback } from 'components/compose-input-field/utils';
import ContactTag from 'components/compose-input-field/ContactTag';
import { ReactTagsFocus } from 'components/compose-input-field/ToField';
import { AutoSaveDraftHandler } from 'components/compose-form';
import { composeMailFormCcValidator } from 'utils/composeMailFormHelper';
import { usePageTranslation } from 'translation/hooks';
import { updateIsOpen, updateModalName, updateModalProps } from 'slices/app';
import { updateModalOpener } from 'components/common/focus/FocusContext';

const CcField: React.FC<AutoSaveDraftHandler> = ({ autoSaveDraftHandler }) => {
  const { t } = usePageTranslation('composeCopy');
  const activeTabIndex = useAppSelector((state) => state.composeSlice.activeTabIndex);
  const composeCollection = useAppSelector((state) => state.composeSlice.composeCollection);
  const composeForm = composeCollection[activeTabIndex];
  const dispatch = useAppDispatch();
  const ccFieldRef = useRef<ReactTagsFocus>(null);
  const ccFieldTags = composeCollection[activeTabIndex]?.cc;
  const [suggestions, suggestionsWithContacts, suggestionsWithLabels, onContactSearch] = useContactSearch(ccFieldTags);
  const updateModalRef = updateModalOpener();

  useEffect(() => {
    if (ccFieldRef) {
      ccFieldRef.current?.focusInput();
    }
  }, []);

  useEffect(() => {
    if (
      composeCollection[activeTabIndex] &&
      composeCollection[activeTabIndex].isChanged &&
      composeMailFormCcValidator(composeForm) &&
      !composeCollection[activeTabIndex].isSavePending
    ) {
      autoSaveDraftHandler('cc', ccFieldTags, composeCollection[activeTabIndex]);
    }
  }, [ccFieldTags?.length]);

  const onDelete = useCallback((tagIndex) => {
    dispatch(removeCcByIndex(tagIndex));
  }, []);

  const onAddition = useCallback(
    (newTag: Tag): void => {
      if (ccFieldTags?.find((c) => c.address === newTag.name.trim())) {
        dispatch(
          updateComposeFormError({
            type: 'error',
            index: activeTabIndex,
            errorIndex: 'compose_form_duplicate_emails',
            message: 'composeFormDuplicateEmails',
          })
        );
        return;
      }
      const contact = suggestionsWithContacts?.find((c) => c.id === newTag.id);
      if (contact) {
        const updatedTag = {
          name: getDisplayNameFallback(contact),
          address: newTag.name.trim(),
        };
        dispatch(addCc(updatedTag));
        return;
      }

      const label = suggestionsWithLabels?.find((l) => l.id === newTag.id);
      if (label) {
        label.contacts.forEach((contact) => {
          contact.emails?.forEach((email) => {
            if (!ccFieldTags?.find((c) => c.address === email.emailAddress)) {
              const updatedTag = {
                name: getDisplayNameFallback(contact),
                address: email.emailAddress,
              };
              dispatch(addCc(updatedTag));
            }
          });
        });

        return;
      }

      const updatedTag = splitEmailDisplayName(newTag.name.trim());

      dispatch(addCc(updatedTag));
    },
    [suggestions]
  );

  const ccFieldFormatted = ccFieldTags?.map((tag, index) => {
    return { id: index, name: tag.name || tag.address, email: tag.address };
  });

  const closeCcField = (): void => {
    dispatch(resetCc());
    dispatch(updateIsCcFieldEnabled(false));
  };

  const handleOpenAddressBook = (): void => {
    dispatch(updateModalProps({ inputField: 'cc', isAddressBook: true }));
    dispatch(updateModalName('addressBook'));
    dispatch(updateIsOpen(true));
  };

  return (
    <span className={styles.inputFieldContainer}>
      <span className={styles.label}>
        <label className={styles.label} htmlFor="input">
          <KiteButton
            type="button"
            variant={'borderless'}
            className={styles.addressBookButton}
            onClick={(e) => {
              updateModalRef(e.currentTarget);
              handleOpenAddressBook();
            }}
            aria-label="import contacts from address book"
          >
            {t('ccField')}
          </KiteButton>
        </label>
      </span>
      <span role="input" className={styles.tagInputList}>
        <ReactTags
          allowNew={true}
          addOnBlur
          tags={ccFieldFormatted}
          suggestions={suggestions}
          onDelete={onDelete}
          placeholderText={''}
          ariaLabelText={t('ccField')}
          onAddition={onAddition}
          onInput={onContactSearch}
          minQueryLength={3}
          suggestionsFilter={() => true}
          tagComponent={ContactTag}
          suggestionComponent={getSuggestionComponent(suggestionsWithContacts || [], suggestionsWithLabels || [])}
          ref={ccFieldRef}
        />
      </span>
      <span className={styles.actionIcons}>
        <button type="button" className={styles.xButton} onClick={closeCcField} aria-label="remove carbon copy">
          <KiteIcon icon="ki-x" size="18px" />
        </button>
      </span>
    </span>
  );
};

export default CcField;
